import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, Wallet, X } from "lucide-react";
import { NAV_LINKS, OFFICIAL_SITE } from "../../data/site";
import { useLanguage } from "../../i18n/LanguageContext";
import { LanguageMenu } from "./LanguageMenu";

export const ConnectWalletButton = ({ className = "" }) => {
    const { t } = useLanguage();
    return (
        <button
            type="button"
            data-testid="connect-wallet-button"
            onClick={() => {
                /* wallet integration to be added by backend developer */
            }}
            className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-white/10 bg-nx-elev px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-nx-text transition-colors duration-300 hover:border-nx-lime/60 hover:text-nx-lime focus:outline-none focus:ring-2 focus:ring-nx-lime ${className}`}
        >
            <Wallet size={14} strokeWidth={1.5} aria-hidden="true" />
            <span>{t("Connect Wallet")}</span>
        </button>
    );
};

export const Navbar = () => {
    const { t } = useLanguage();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 64);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
                scrolled ? "border-b border-white/5 bg-nx-base/85 backdrop-blur-lg" : "border-b border-transparent"
            }`}
        >
            <div className="relative mx-auto flex h-16 max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:h-[72px] lg:px-12">
                <a href="#top" data-testid="nav-logo" className="flex shrink-0 items-center" aria-label="Nexora Global home">
                    <img src="/assets/nexora-logo.png" alt="Nexora Global" className="h-6 w-auto sm:h-7" />
                </a>

                <nav className="hidden items-center gap-7 lg:flex xl:gap-8" aria-label="Primary">
                    {NAV_LINKS.map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="text-sm font-semibold text-nx-sub transition-colors duration-300 hover:text-nx-text"
                        >
                            {t(link.label)}
                        </a>
                    ))}
                </nav>

                <div className="hidden items-center gap-5 lg:flex">
                    <a
                        href="#community"
                        data-testid="nav-link-community"
                        className="text-sm font-semibold text-nx-sub transition-colors duration-300 hover:text-nx-text"
                    >
                        {t("Community")}
                    </a>
                    <a
                        href={OFFICIAL_SITE}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-link-documentation"
                        className="text-sm font-semibold text-nx-sub transition-colors duration-300 hover:text-nx-text"
                    >
                        {t("Documentation")}
                    </a>
                    <a
                        href="/dashboard"
                        data-testid="nav-link-dashboard"
                        className="text-sm font-semibold text-nx-sub transition-colors duration-300 hover:text-nx-text"
                    >
                        Dashboard
                    </a>
                    <LanguageMenu />
                    <AnimatePresence>
                        {scrolled && (
                            <motion.div
                                initial={{ opacity: 0, x: 12 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 12 }}
                                transition={{ duration: 0.3 }}
                            >
                                <ConnectWalletButton />
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <a
                        href="#join"
                        data-testid="nav-join-button"
                        className="btn-sweep group inline-flex items-center gap-1.5 rounded-full bg-nx-lime px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.08em] text-nx-base shadow-[0_4px_24px_rgba(184,255,24,0.18)] transition-all duration-300 hover:brightness-110 hover:-translate-y-px"
                    >
                        {t("Join Nexora")}
                        <ArrowUpRight size={14} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                    </a>
                </div>

                <div className="flex items-center gap-3 lg:hidden">
                    <AnimatePresence>
                        {scrolled && (
                            <motion.div
                                initial={{ opacity: 0, x: 12 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 12 }}
                                transition={{ duration: 0.3 }}
                            >
                                <ConnectWalletButton className="px-3 py-1.5 text-[10px]" />
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <button
                        type="button"
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-nx-elev text-nx-text"
                    >
                        {open ? <X size={18} strokeWidth={1.5} /> : <Menu size={18} strokeWidth={1.5} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden border-b border-white/5 bg-nx-base/95 backdrop-blur-lg lg:hidden"
                        aria-label="Mobile"
                    >
                        <div className="flex flex-col gap-1 px-5 py-4">
                            {NAV_LINKS.map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => setOpen(false)}
                                    className="rounded-lg px-3 py-3 text-sm font-semibold text-nx-sub transition-colors hover:bg-white/5 hover:text-nx-text"
                                >
                                    {t(link.label)}
                                </a>
                            ))}
                            <a
                                href="#community"
                                data-testid="mobile-nav-link-community"
                                onClick={() => setOpen(false)}
                                className="rounded-lg px-3 py-3 text-sm font-semibold text-nx-sub transition-colors hover:bg-white/5 hover:text-nx-text"
                            >
                                {t("Community")}
                            </a>
                            <a
                                href={OFFICIAL_SITE}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="mobile-nav-link-documentation"
                                className="rounded-lg px-3 py-3 text-sm font-semibold text-nx-sub transition-colors hover:bg-white/5 hover:text-nx-text"
                            >
                                {t("Documentation")}
                            </a>
                            <a
                                href="/dashboard"
                                data-testid="mobile-nav-link-dashboard"
                                onClick={() => setOpen(false)}
                                className="rounded-lg px-3 py-3 text-sm font-semibold text-nx-sub transition-colors hover:bg-white/5 hover:text-nx-text"
                            >
                                Dashboard
                            </a>
                            <a
                                href="#join"
                                data-testid="mobile-nav-join-button"
                                onClick={() => setOpen(false)}
                                className="btn-sweep mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-nx-lime px-5 py-3 text-xs font-extrabold uppercase tracking-[0.08em] text-nx-base"
                            >
                                {t("Join Nexora")}
                                <ArrowUpRight size={14} strokeWidth={2} aria-hidden="true" />
                            </a>
                            <LanguageMenu mobile />
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
